import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import arrowUpRightIcon from "../assets/figma-documents/arrow-up-right.svg";
import circleAlertIcon from "../assets/figma-documents/circle-alert.svg";
import circleAlertFixIcon from "../assets/figma-documents/circle-alert-fix.svg";
import trashIcon from "../assets/figma-documents/trash.svg";
import closeIcon from "../assets/figma-documents/close.svg";
import { useAuth } from "../auth/OAuthContext";
import { apiClient, ApiError, uploadToPresignedUrl } from "../api/client";
import { OrderFlowShell } from "../components/OrderFlowShell";
import { FigmaStatusChip } from "../components/DocumentsTable";
import { useDocumentRealtimeInvalidation } from "../hooks/useDocumentRealtime";
import {
  AuditLogRow,
  DocumentFixNoteRow,
  DocumentRow,
  DocumentVersionRow,
} from "../types";

interface SealVerification {
  valid: boolean;
  sealedPdfHashMatches: boolean;
  sealSignatureValid: boolean;
  expectedSealedSha256Hex: string | null;
  actualSealedSha256Hex: string | null;
}

export function DocumentReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { idToken, roles } = useAuth();

  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] =
    useState("application/pdf");
  const [doc, setDoc] = useState<DocumentRow | null>(null);

  const [auditTrail, setAuditTrail] = useState<AuditLogRow[]>([]);
  const [versions, setVersions] = useState<DocumentVersionRow[]>([]);
  const [fixNotes, setFixNotes] = useState<DocumentFixNoteRow[]>([]);
  const [sealVerification, setSealVerification] =
    useState<SealVerification | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [signing, setSigning] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [acknowledgeWarnings, setAcknowledgeWarnings] = useState(false);

  // Modals state
  const [showFixModal, setShowFixModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const [selectedVersion, setSelectedVersion] =
    useState<DocumentVersionRow | null>(null);
  const [fixNoteText, setFixNoteText] = useState("");
  const [rejectionReasonText, setRejectionReasonText] = useState("");
  const [reuploadFile, setReuploadFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isDoctor = roles.includes("Doctor");
  const isNurse = roles.includes("Nurse");

  const load = useCallback(async () => {
    if (!idToken || !id) return;
    const client = apiClient(idToken);
    try {
      let nextError: string | null = null;
      const documentRes = await client.getDocument(id);
      setDoc(documentRes.document);
      const [trailRes, versionsRes, fixNotesRes] = await Promise.allSettled([
        client.getAuditTrail(id),
        client.listVersions(id),
        client.listFixNotes(id),
      ]);
      setAuditTrail(
        trailRes.status === "fulfilled" ? trailRes.value.events : [],
      );
      setVersions(
        versionsRes.status === "fulfilled" ? versionsRes.value.versions : [],
      );
      setFixNotes(
        fixNotesRes.status === "fulfilled" ? fixNotesRes.value.fixNotes : [],
      );
      if (
        [trailRes, versionsRes, fixNotesRes].some(
          (result) => result.status === "rejected",
        )
      ) {
        nextError =
          "Some document history could not be loaded. Refresh to try again.";
      }
      try {
        const dl = await client.getDownloadUrl(id);
        setDownloadUrl(dl.downloadUrl);
        setPreviewContentType(dl.contentType);
      } catch (err) {
        setDownloadUrl(null);
        setPreviewContentType("application/pdf");
        nextError = err instanceof Error ? err.message : String(err);
      }

      if (documentRes.document.sealed_key) {
        try {
          setSealVerification(await client.verifySeal(documentRes.document.id));
        } catch {
          setSealVerification(null);
        }
      } else {
        setSealVerification(null);
      }

      setError(nextError);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [idToken, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const shouldRefreshForRealtimeEvent = useCallback(
    (event: { documentId: string }) => event.documentId === id,
    [id],
  );

  useDocumentRealtimeInvalidation(
    idToken,
    () => load(),
    shouldRefreshForRealtimeEvent,
  );

  async function handleExtract() {
    if (!idToken || !id) return;
    setExtracting(true);
    setError(null);
    try {
      await apiClient(idToken).extract(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExtracting(false);
    }
  }

  async function handleSign() {
    if (!idToken || !id) return;
    setSigning(true);
    setError(null);
    try {
      await apiClient(idToken).sign(id, acknowledgeWarnings);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSigning(false);
    }
  }

  async function handleFixSubmit(e: FormEvent) {
    e.preventDefault();
    if (!idToken || !id || !fixNoteText.trim()) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient(idToken).requestFix(id, fixNoteText.trim());
      setShowFixModal(false);
      setFixNoteText("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionBusy(false);
    }
  }

  async function handleRejectSubmit(e: FormEvent) {
    e.preventDefault();
    if (!idToken || !id || !rejectionReasonText.trim()) return;
    setActionBusy(true);
    setError(null);
    try {
      await apiClient(idToken).reject(id, rejectionReasonText.trim());
      setShowRejectModal(false);
      setRejectionReasonText("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionBusy(false);
    }
  }

  async function handleReupload(e: FormEvent) {
    e.preventDefault();
    if (!idToken || !id || !reuploadFile) return;
    setActionBusy(true);
    setError(null);
    try {
      const client = apiClient(idToken);
      // reuploadUrl now returns the same documentId — no new document row is created.
      const { uploadUrl, documentId: uploadDocId } =
        await client.requestReuploadUrl(id, {
          fileName: reuploadFile.name,
          contentType: reuploadFile.type || "application/pdf",
        });
      await uploadToPresignedUrl(uploadUrl, reuploadFile);
      const { documentId: canonicalId } =
        await client.completeUpload(uploadDocId);
      setReuploadFile(null);
      if (canonicalId !== id) {
        // Legacy path: backend migrated a child doc back onto the parent.
        // Navigate to the canonical parent document.
        navigate(`/documents/${canonicalId}`, { replace: true });
      } else {
        // New path: same document updated in-place — just reload.
        await load();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionBusy(false);
    }
  }

  const complianceStatus = doc?.compliance_status;
  const warnings: string[] = doc?.compliance_warnings ?? [];
  const latestFixNote =
    fixNotes[0]?.note_text ??
    doc?.latest_fix_note_text ??
    "Please fix and re-upload the document.";
  const canDoctorAct = isDoctor && doc?.status === "pending";
  const doctorActionBusy = extracting || signing || actionBusy;
  const canReupload = isNurse && doc?.status === "fix_requested";
  // A document is in "revision pending" state when it was previously fix_requested
  // and the nurse has re-uploaded (status back to pending).
  const isRevisionPending =
    doc?.status === "pending" && !!doc?.fix_requested_at;
  const hasBeenExtracted = !!doc?.extracted_data;

  const sealDocument = doc?.sealed_key ? doc : null;
  const sealPayload = sealDocument?.seal_payload as
    | { verificationUrl?: string; sealedSha256Hex?: string }
    | undefined;

  const isPdf =
    previewContentType === "application/pdf" ||
    Boolean(doc?.sealed_key) ||
    (downloadUrl ? downloadUrl.toLowerCase().includes(".pdf") : false);
  const previewUrl = downloadUrl
    ? isPdf
      ? `${downloadUrl.split("#")[0]}#zoom=33`
      : downloadUrl
    : null;

  return (
    <OrderFlowShell
      title="Review document"
      subtitle="Review all documents routed to your physician organization."
      breadcrumb="/ Dashboard / Review document"
    >
      {error && (
        <div className="mb-6 p-4 rounded-2xl bg-[#F9EEEE] border border-[#D83933]/20 text-[#D83933] text-sm font-semibold">
          {error}
        </div>
      )}

      {/* Main Review Section */}
      <div className="document-review-layout grid grid-cols-1 lg:grid-cols-[minmax(0,700px)_minmax(0,1fr)] gap-4 items-stretch mb-4 font-sans">
        {/* PDF / Image Document Viewer */}
        <div className="document-review-preview bg-[#E6E8EA] rounded-[32px] overflow-hidden min-h-[574px] h-full flex flex-col">
          {downloadUrl ? (
            !isPdf ? (
              <div className="flex-1 flex items-center justify-center p-4 bg-[#F2F4FA] overflow-auto">
                <img
                  key={downloadUrl}
                  src={downloadUrl}
                  alt="Document Preview"
                  className="max-h-[750px] max-w-full object-contain rounded-xl shadow-xs"
                />
              </div>
            ) : (
              <iframe
                key={downloadUrl}
                src={previewUrl ?? downloadUrl}
                className="w-full flex-1 border-0 min-h-[574px]"
                title="Document Preview"
              />
            )
          ) : (
            <div className="flex-1 flex items-center justify-center text-[#7F8A92] text-sm font-medium bg-[#E6E8EA]">
              No document preview available.
            </div>
          )}
        </div>

        {/* Right Info Rail (Figma 35:244 / 35:719 / 35:1507 / 35:1991) */}
        <div className="document-review-rail flex flex-col gap-4">
          {doc && (
            <>
              {/* Card 1: Document details & status (Figma 35:244 / 35:1991) */}
              <div className="review-info-card bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xl font-semibold text-[#011627] font-display truncate">
                    Document details
                  </h3>
                  <FigmaStatusChip
                    status={doc.status}
                    revisionPending={isRevisionPending}
                    review
                    className="shrink-0"
                  />
                </div>

                <div className="flex flex-col gap-2 text-base">
                  <div className="flex items-center justify-between">
                    <span className="text-[#7F8A92] font-normal">Created</span>
                    <strong className="text-[#2D2E2F] font-bold">
                      {formatDate(doc.created_at)}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#7F8A92] font-normal">
                      Sending nurse
                    </span>
                    <strong className="text-[#2D2E2F] font-bold">
                      {doc.nurse_name || doc.nurse_email || "Unknown nurse"}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#7F8A92] font-normal">Doctor</span>
                    <strong className="text-[#2D2E2F] font-bold">
                      {doc.doctor_name || doc.doctor_email || "Not selected"}
                    </strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#7F8A92] font-normal">Patient</span>
                    <strong className="text-[#2D2E2F] font-bold">
                      {doc.patient_ref || "Untitled Patient"}
                    </strong>
                  </div>
                  {doc.organization_name && (
                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8A92] font-normal">
                        Location
                      </span>
                      <strong className="text-[#2D2E2F] font-bold">
                        {doc.organization_name}
                      </strong>
                    </div>
                  )}

                  {/* Doctor Action Buttons (Figma 35:244 / 35:719 / 35:1507) */}
                  {canDoctorAct && (
                    <div className="flex flex-col gap-3">
                      {complianceStatus === "exception" && (
                        <label className="flex items-start gap-2.5 text-xs text-[#CF4900] bg-[#FFF3EA] p-3 rounded-xl border border-[#CF4900]/20 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={acknowledgeWarnings}
                            onChange={(e) =>
                              setAcknowledgeWarnings(e.target.checked)
                            }
                            className="mt-0.5 rounded text-[#012699]"
                          />
                          <span className="font-bold">
                            I reviewed the compliance warnings.
                          </span>
                        </label>
                      )}

                      {/* Sign & approve (Figma 35:415 - EL-e3b8cacb: fills: #012699, borderRadius: 32px) */}
                      <button
                        type="button"
                        className="w-full min-h-[38px] flex items-center justify-center font-bold text-base text-white bg-[#012699] hover:bg-[#0B2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all cursor-pointer"
                        onClick={handleSign}
                        disabled={
                          doctorActionBusy ||
                          (complianceStatus === "exception" &&
                            !acknowledgeWarnings)
                        }
                      >
                        {signing ? "Signing..." : "Sign & approve"}
                      </button>

                      {/* Send fix required (Figma 35:416 - EL-b23589a0: bg #FDFFFC, border #0B2981, borderRadius: 32px) */}
                      <button
                        type="button"
                        className="w-full min-h-[38px] flex items-center justify-center font-bold text-base text-[#0B2981] bg-[#FDFFFC] hover:bg-[#F2F4FA] border border-[#0B2981] rounded-full transition-all cursor-pointer"
                        onClick={() => {
                          setError(null);
                          setShowFixModal(true);
                        }}
                        disabled={doctorActionBusy}
                      >
                        Send fix required
                      </button>

                      {/* Reject and close (Figma 35:417 - EL-42c673c3: bg #F9EEEE, border #D83933, text #D83933, borderRadius: 32px) */}
                      <button
                        type="button"
                        className="w-full min-h-[38px] flex items-center justify-center font-bold text-base text-[#D83933] bg-[#F9EEEE] hover:bg-[#F9EEEE]/80 border border-[#D83933] rounded-full transition-all cursor-pointer"
                        onClick={() => {
                          setError(null);
                          setShowRejectModal(true);
                        }}
                        disabled={doctorActionBusy}
                      >
                        Reject and close
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 2: Classification (Figma 35:244 / 35:1991) */}
              <div className="review-info-card bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-[#011627] font-display">
                    Classification
                  </h3>
                </div>

                {!hasBeenExtracted && !isNurse ? (
                  <div className="flex w-full flex-col gap-4">
                    <div className="flex w-full flex-col items-center justify-center gap-4 py-1 text-center">
                      {/* Circle icon container (Figma 35:423 - EL-96e81f8d) */}
                      <div className="w-14 h-14 rounded-full bg-[#FDFFFC] border border-[#E6E8EA] flex items-center justify-center shrink-0">
                        <img
                          src={circleAlertIcon}
                          className="block h-6 w-6"
                          alt=""
                          aria-hidden="true"
                        />
                      </div>
                      <p className="w-full text-base text-[#7F8A92] font-normal text-center">
                        No classification saved.
                      </p>
                    </div>
                    {isDoctor && doc.status === "pending" && (
                      <button
                        type="button"
                        className="w-full py-2.5 px-6 rounded-full text-base font-bold text-white bg-[#012699] hover:bg-[#0B2981] transition-all cursor-pointer shadow-xs"
                        onClick={handleExtract}
                        disabled={extracting}
                      >
                        {extracting ? "Analyzing..." : "Run analysis"}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 text-base">
                    <div className="font-bold text-[#011627] text-lg">
                      {doc.extracted_data?.documentClassification ||
                        (complianceStatus === "ready_to_sign"
                          ? "Ready to sign"
                          : complianceStatus === "exception"
                            ? "Exception - review required"
                            : "Clinical Document")}
                    </div>

                    <p className="text-[#7F8A92] leading-relaxed text-sm font-normal">
                      {doc.extracted_data?.notes ||
                        doc.extracted_data?.billingAdvisory ||
                        "Review the extracted text and original document before signing."}
                    </p>

                    {warnings.length > 0 && (
                      <div className="p-3.5 rounded-xl bg-[#FFF3EA] border border-[#CF4900]/20 text-[#CF4900]">
                        <span className="font-bold block mb-1">Warnings:</span>
                        <ul className="list-disc pl-4 space-y-1 text-xs">
                          {warnings.map((w, idx) => (
                            <li key={idx}>{w}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {isDoctor && doc.status === "pending" && (
                      <button
                        type="button"
                        className="mt-1 py-2.5 px-6 rounded-full text-sm font-bold text-white bg-[#012699] hover:bg-[#0B2981] transition-all cursor-pointer self-start shadow-xs"
                        onClick={handleExtract}
                        disabled={extracting}
                      >
                        {extracting ? "Analyzing..." : "Re-run analysis"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Card 3: Seal (Figma 35:591 / 35:1991 / 35:1992 - Approved State) */}
              {sealDocument && (
                <div className="review-info-card bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-[#011627] font-display">
                      Seal
                    </h3>
                  </div>

                  <div className="flex flex-col gap-3 text-base">
                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8A92] font-normal">
                        PDF hash
                      </span>
                      <strong className="text-[#2D2E2F] font-bold">
                        {sealVerification
                          ? sealVerification.sealedPdfHashMatches
                            ? "Matched"
                            : "Mismatch"
                          : "Unavailable"}
                      </strong>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8A92] font-normal">
                        Signature
                      </span>
                      <strong className="text-[#2D2E2F] font-bold">
                        {sealVerification
                          ? sealVerification.sealSignatureValid
                            ? "Verified"
                            : "Invalid"
                          : "Unavailable"}
                      </strong>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-[#7F8A92] font-normal">
                        SHA-256
                      </span>
                      <strong className="font-mono text-xs text-[#2D2E2F] font-bold truncate max-w-[180px]">
                        {sealDocument.sealed_sha256_hex?.slice(0, 32) ||
                          "Unavailable"}
                      </strong>
                    </div>

                    {sealPayload?.verificationUrl && (
                      <div className="pt-2 border-t border-[#E6E8EA] flex items-center justify-between">
                        <span className="text-[#7F8A92] font-normal">
                          QR target
                        </span>
                        <a
                          href={sealPayload.verificationUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-bold text-[#012699] hover:underline text-sm"
                        >
                          <span>Open verification</span>
                          <img
                            src={arrowUpRightIcon}
                            alt=""
                            aria-hidden="true"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Card 4: Fix note card (Figma 35:719 / 35:1076) */}
              {doc.status === "fix_requested" && !canReupload && (
                <div className="review-info-card bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-3">
                  <h3 className="text-xl font-bold text-[#011627] font-display">
                    Fix note
                  </h3>
                  <p className="text-base text-[#7F8A92] leading-relaxed font-normal">
                    {latestFixNote}
                  </p>
                </div>
              )}

              {/* Card 5: Rejection note card (Figma 35:1507 / 35:1864) */}
              {doc.status === "rejected" && (
                <div className="review-info-card bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-3">
                  <h3 className="text-xl font-bold text-[#011627] font-display">
                    Rejection note
                  </h3>
                  <p className="text-base text-[#7F8A92] leading-relaxed font-normal">
                    {doc.rejection_reason ||
                      "Document was reviewed and rejected."}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Re-upload section for Nurses (Figma 37:6402 / 37:6527 - HelpText Banner with 5px Left Border) */}
      {canReupload && (
        <section
          className="review-reupload mb-8 flex flex-col gap-4 font-sans"
          aria-label="Submit a corrected document"
        >
          {/* Figma HelpText component #37:6606 */}
          <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#FDFFFC] border border-[#CF4900] border-l-[5px] text-[#011627] shadow-xs">
            <img
              src={circleAlertFixIcon}
              className="shrink-0 mt-0.5"
              alt=""
              aria-hidden="true"
            />
            <div className="flex flex-col gap-2">
              <strong className="text-lg font-bold text-[#2D2E2F] font-display">
                Apply the fixes mentioned below and re-upload the document here!
              </strong>
              <p className="text-base text-[#011627] font-normal">
                {latestFixNote}
              </p>
            </div>
          </div>

          <form onSubmit={handleReupload} className="flex flex-col gap-4">
            <div
              className="relative border-2 border-dashed border-[#CCD5EC] hover:border-[#012699] rounded-2xl p-8 text-center cursor-pointer transition-all bg-[#FAFBFD]"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                setReuploadFile(event.dataTransfer.files?.[0] ?? null);
              }}
            >
              <input
                type="file"
                id="reuploadInput"
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                accept="application/pdf,image/png,image/jpeg"
                onChange={(event) => {
                  setReuploadFile(event.target.files?.[0] ?? null);
                  event.target.value = "";
                }}
              />
              <div className="flex flex-col items-center gap-1 pointer-events-none">
                <span className="text-sm text-[#7F8A92]">
                  Click to browse or
                </span>
                <span className="text-sm text-[#7F8A92]">
                  drag and drop your files
                </span>
              </div>
            </div>

            {reuploadFile && (
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-[#F2F4FA] border border-[#E6E8EA]">
                <span className="text-sm font-bold text-[#011627] truncate max-w-[280px]">
                  {reuploadFile.name}
                </span>
                <button
                  type="button"
                  className="p-1.5 rounded-lg text-[#7F8A92] hover:text-[#D83933] hover:bg-white transition-colors"
                  onClick={() => setReuploadFile(null)}
                  aria-label="Remove file"
                >
                  <img src={trashIcon} alt="" aria-hidden="true" />
                </button>
              </div>
            )}

            <button
              type="submit"
              className="w-full sm:w-auto px-4 min-h-[38px] flex items-center justify-center font-bold text-base text-white bg-[#012699] hover:bg-[#0B2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all cursor-pointer self-start"
              disabled={actionBusy || !reuploadFile}
            >
              {actionBusy ? "Uploading..." : "Upload for review"}
            </button>
          </form>
        </section>
      )}

      {/* Revision History & Audit Trail Bottom Tables (Figma 35:428 / 35:439 - width: 1140px stacked) */}
      <div className="flex flex-col gap-6 sm:gap-8 font-sans">
        {/* Fix Note History (Revision History) */}
        <section className="flex flex-col gap-3">
          <h3 className="text-xl font-bold text-[#011627] font-display">
            Revision history
          </h3>
          <div className="bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[#E6E8EA] bg-[#FDFFFC] text-base font-bold text-[#011627]">
                    <th className="py-4 px-6 font-bold">Fix note</th>
                    <th className="py-4 px-6 font-bold">Resolved</th>
                    <th className="py-4 px-6 font-bold">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E8EA]">
                  {fixNotes.length === 0 && versions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="py-10 text-center text-[#7F8A92] font-medium text-base"
                      >
                        No revision history for this document yet.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {fixNotes.map((note) => (
                        <tr
                          key={note.id}
                          className="hover:bg-[#F2F4FA]/40 transition-colors"
                        >
                          <td className="py-4 px-6">
                            <p className="text-sm text-[#011627] leading-relaxed max-w-md">
                              {note.note_text}
                            </p>
                          </td>
                          <td className="py-4 px-6">
                            {note.resolved_document_id ? (
                              <span className="inline-flex px-3.5 py-1 rounded-full text-xs font-bold bg-[#E3F5E1] text-[#008817]">
                                Resolved
                              </span>
                            ) : (
                              <span className="inline-flex px-3.5 py-1 rounded-full text-xs font-bold bg-[#F9EEEE] text-[#D83933]">
                                Open
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-6 text-sm text-[#7F8A92] whitespace-nowrap">
                            {formatDate(note.created_at)}
                          </td>
                        </tr>
                      ))}
                      {versions.map((ver) => (
                        <tr
                          key={ver.id}
                          className="hover:bg-[#F2F4FA]/40 transition-colors"
                        >
                          <td className="py-4 px-6">
                            <strong className="text-base font-bold text-[#011627] block">
                              Version {ver.version_number}
                            </strong>
                            {ver.change_summary && (
                              <div className="text-sm text-[#7F8A92] mt-0.5">
                                {ver.change_summary}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <span className="inline-flex px-3.5 py-1 rounded-full text-xs font-bold bg-[#FFF3EA] text-[#CF4900]">
                              {ver.is_signed
                                ? "Signed"
                                : ver.is_amendment
                                  ? "Amendment"
                                  : "Draft"}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-sm text-[#7F8A92] whitespace-nowrap">
                            {formatDate(ver.created_at)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Audit Trail Table */}
        <section className="flex flex-col gap-3">
          <h3 className="text-xl font-bold text-[#011627] font-display">
            Audit trail
          </h3>
          <div className="bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-[#E6E8EA] bg-[#FDFFFC] text-base font-bold text-[#011627]">
                    <th className="py-4 px-6 font-bold">When</th>
                    <th className="py-4 px-6 font-bold">Action</th>
                    <th className="py-4 px-6 font-bold">Actor</th>
                    <th className="py-4 px-6 font-bold">SHA-256</th>
                    <th className="py-4 px-6 font-bold">Row hash</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E6E8EA]">
                  {auditTrail.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-10 text-center text-[#7F8A92] font-medium text-base"
                      >
                        No audit events recorded yet.
                      </td>
                    </tr>
                  ) : (
                    auditTrail.map((event) => {
                      const actionInfo = formatAuditAction(event.action);
                      const actor = getActorDisplay(event, doc);
                      return (
                        <tr
                          key={event.id}
                          className="hover:bg-[#F2F4FA]/40 transition-colors"
                        >
                          <td className="py-4 px-6 text-[#7F8A92] text-sm whitespace-nowrap">
                            {formatDate(event.created_at)}
                          </td>
                          <td className="py-4 px-6 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold leading-none ${actionInfo.bg} ${actionInfo.text}`}
                            >
                              {actionInfo.label}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-bold text-[#011627] text-sm leading-tight">
                              {actor.primary}
                            </div>
                            {actor.secondary && (
                              <div className="text-xs text-[#7F8A92] mt-0.5 font-normal">
                                {actor.secondary}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-6 font-mono text-xs text-[#7F8A92]">
                            {event.sha256_hex?.slice(0, 16) ?? "—"}
                          </td>
                          <td className="py-4 px-6 font-mono text-xs text-[#7F8A92]">
                            {event.row_hash?.slice(0, 16) ?? "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      {selectedVersion && (
        <div
          className="fixed inset-0 z-50 bg-[#011627]/35 backdrop-blur-[2px] flex items-center justify-center p-4 font-sans"
          onClick={() => setSelectedVersion(null)}
        >
          <div
            className="w-full max-w-[760px] max-h-[calc(100dvh-32px)] overflow-hidden bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] shadow-[0_24px_80px_-24px_rgba(1,22,39,0.4)] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="revision-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-[#E6E8EA]">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#7F8A92]">
                  Revision history
                </span>
                <h3
                  id="revision-dialog-title"
                  className="text-2xl font-bold text-[#011627] font-display mt-1"
                >
                  Version {selectedVersion.version_number}
                </h3>
                <p className="text-sm text-[#7F8A92] mt-1">
                  Saved {formatDate(selectedVersion.created_at)}
                </p>
              </div>
              <button
                type="button"
                className="p-2 rounded-full hover:bg-[#F2F4FA] cursor-pointer"
                onClick={() => setSelectedVersion(null)}
                aria-label="Close revision"
              >
                <img src={closeIcon} alt="" aria-hidden="true" />
              </button>
            </div>
            <div className="overflow-y-auto p-5 sm:p-6 flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <RevisionMeta
                  label="Status"
                  value={
                    selectedVersion.is_signed
                      ? "Signed"
                      : selectedVersion.is_amendment
                        ? "Amendment"
                        : "Draft"
                  }
                />
                <RevisionMeta
                  label="Created by"
                  value={selectedVersion.created_by}
                  mono
                />
                <RevisionMeta
                  label="Revision ID"
                  value={selectedVersion.id}
                  mono
                />
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#0B2981] mb-2">
                  Change summary
                </h4>
                <div className="rounded-2xl border border-[#E6E8EA] bg-[#F7F8FA] p-4 text-sm text-[#24313D]">
                  {selectedVersion.change_summary ||
                    "No change summary was recorded for this revision."}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-[#0B2981] mb-2">
                  Saved document data
                </h4>
                <div className="rounded-2xl border border-[#E6E8EA] overflow-hidden divide-y divide-[#E6E8EA]">
                  {Object.entries(selectedVersion.data ?? {}).length > 0 ? (
                    Object.entries(selectedVersion.data).map(([key, value]) => (
                      <div
                        key={key}
                        className="grid grid-cols-1 sm:grid-cols-[190px_minmax(0,1fr)] gap-1 sm:gap-4 px-4 py-3 bg-white"
                      >
                        <span className="text-xs font-bold text-[#7F8A92] break-words">
                          {humanizeField(key)}
                        </span>
                        <span className="text-sm font-semibold text-[#24313D] break-words">
                          {displayRevisionValue(value)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="p-5 text-sm text-[#7F8A92]">
                      No structured data was saved with this revision.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="p-5 sm:p-6 border-t border-[#E6E8EA] flex justify-end">
              <button
                type="button"
                className="px-6 min-h-[40px] rounded-full bg-[#012699] hover:bg-[#0B2981] text-white text-sm font-bold cursor-pointer"
                onClick={() => setSelectedVersion(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Doctor Fix Required (Figma 35:719 / 35:1064) */}
      {showFixModal && (
        <div
          className="fixed inset-0 z-50 bg-white/80 flex items-center justify-center p-4 font-sans"
          onClick={() => setShowFixModal(false)}
        >
          <div
            className="w-full max-w-[700px] bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fix-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3
                id="fix-dialog-title"
                className="text-xl font-semibold text-[#011627] font-display"
              >
                What should the nurse fix
              </h3>
              <button
                type="button"
                className="p-1 rounded-lg text-[#7F8A92] hover:text-[#011627] transition-colors cursor-pointer"
                onClick={() => setShowFixModal(false)}
                aria-label="Close"
              >
                <img src={closeIcon} alt="" aria-hidden="true" />
              </button>
            </div>
            <form onSubmit={handleFixSubmit} className="flex flex-col gap-5">
              {error && (
                <div className="p-3 rounded-xl bg-[#F9EEEE] border border-[#D83933]/20 text-[#D83933] text-sm font-semibold">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="doctor-fix-note"
                  className="text-sm font-bold text-[#0B2981]"
                >
                  Fix note
                </label>
                <textarea
                  id="doctor-fix-note"
                  className="w-full p-4 text-base bg-[#FDFFFC] border border-[#E6E8EA] rounded-[32px] outline-none focus:border-[#012699] focus:ring-2 focus:ring-[#012699]/15 text-[#011627] placeholder:text-[#A9AEB1] min-h-[96px]"
                  value={fixNoteText}
                  onChange={(e) => setFixNoteText(e.target.value)}
                  placeholder="Write your own personal note for this encounter"
                  rows={4}
                  required
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  className="flex-1 min-h-[38px] text-base font-bold text-[#0B2981] bg-[#FDFFFC] hover:bg-[#F2F4FA] border border-[#0B2981] rounded-full transition-colors cursor-pointer"
                  onClick={() => setShowFixModal(false)}
                  disabled={actionBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 min-h-[38px] text-base font-bold text-white bg-[#012699] hover:bg-[#0B2981] rounded-full transition-all cursor-pointer disabled:opacity-50"
                  disabled={actionBusy || !fixNoteText.trim()}
                >
                  {actionBusy ? "Sending..." : "Send fix required"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Doctor Reject (Figma 35:1507 / 35:1852) */}
      {showRejectModal && (
        <div
          className="fixed inset-0 z-50 bg-white/80 flex items-center justify-center p-4 font-sans"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="w-full max-w-[700px] bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col gap-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3
                id="reject-dialog-title"
                className="text-xl font-semibold text-[#011627] font-display"
              >
                Why this document is rejected?
              </h3>
              <button
                type="button"
                className="p-1 rounded-lg text-[#7F8A92] hover:text-[#011627] transition-colors cursor-pointer"
                onClick={() => setShowRejectModal(false)}
                aria-label="Close"
              >
                <img src={closeIcon} alt="" aria-hidden="true" />
              </button>
            </div>
            <form onSubmit={handleRejectSubmit} className="flex flex-col gap-5">
              {error && (
                <div className="p-3 rounded-xl bg-[#F9EEEE] border border-[#D83933]/20 text-[#D83933] text-sm font-semibold">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="doctor-rejection-note"
                  className="text-sm font-bold text-[#0B2981]"
                >
                  Rejection note
                </label>
                <textarea
                  id="doctor-rejection-note"
                  className="w-full p-4 text-base bg-[#FDFFFC] border border-[#E6E8EA] rounded-[32px] outline-none focus:border-[#012699] focus:ring-2 focus:ring-[#012699]/15 text-[#011627] placeholder:text-[#A9AEB1] min-h-[96px]"
                  value={rejectionReasonText}
                  onChange={(e) => setRejectionReasonText(e.target.value)}
                  placeholder="Write your own personal note for this encounter"
                  rows={4}
                  required
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  className="flex-1 min-h-[38px] text-base font-bold text-[#0B2981] bg-[#FDFFFC] hover:bg-[#F2F4FA] border border-[#0B2981] rounded-full transition-colors cursor-pointer"
                  onClick={() => setShowRejectModal(false)}
                  disabled={actionBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 min-h-[38px] text-base font-bold text-[#D83933] bg-[#F9EEEE] hover:bg-[#F2DADA] border border-[#D83933] rounded-full transition-all cursor-pointer disabled:opacity-50"
                  disabled={actionBusy || !rejectionReasonText.trim()}
                >
                  {actionBusy ? "Rejecting..." : "Reject and close"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </OrderFlowShell>
  );
}

function RevisionMeta({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-xl bg-[#F2F4FA] p-3 min-w-0">
      <span className="block text-[11px] font-bold uppercase tracking-wide text-[#7F8A92]">
        {label}
      </span>
      <strong
        className={`block mt-1 text-sm text-[#011627] break-all ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </strong>
    </div>
  );
}

function humanizeField(value: string): string {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function displayRevisionValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function formatDate(val: string | null): string {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return val;
  }
}

function formatAuditAction(action: string): {
  label: string;
  bg: string;
  text: string;
} {
  switch (action) {
    case "upload_requested":
      return {
        label: "Upload requested",
        bg: "bg-[#F2F4FA]",
        text: "text-[#012699]",
      };
    case "fix_requested":
      return {
        label: "Fix requested",
        bg: "bg-[#F9EEEE]",
        text: "text-[#D83933]",
      };
    case "reuploaded":
      return {
        label: "Re-uploaded",
        bg: "bg-[#F2F4FA]",
        text: "text-[#0B2981]",
      };
    case "signed":
      return {
        label: "Verified & approved",
        bg: "bg-[#E3F5E1]",
        text: "text-[#008817]",
      };
    case "rejected":
      return {
        label: "Rejected",
        bg: "bg-[#F9EEEE]",
        text: "text-[#D83933]",
      };
    case "extracted":
      return {
        label: "AI extracted",
        bg: "bg-[#F2F4FA]",
        text: "text-[#24313D]",
      };
    case "edited":
      return {
        label: "Draft edited",
        bg: "bg-[#FFF3EA]",
        text: "text-[#CF4900]",
      };
    case "amended":
      return {
        label: "Amended",
        bg: "bg-[#FFF3EA]",
        text: "text-[#CF4900]",
      };
    default:
      return {
        label: action
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        bg: "bg-[#F2F4FA]",
        text: "text-[#012699]",
      };
  }
}

function getActorDisplay(
  event: AuditLogRow,
  doc: DocumentRow | null,
): { primary: string; secondary?: string } {
  if (event.actor_name) {
    return {
      primary: event.actor_name,
      secondary:
        event.actor_role || (event.actor_email ? event.actor_email : undefined),
    };
  }
  if (doc) {
    if (event.actor_id === doc.nurse_id) {
      return {
        primary: doc.nurse_name || doc.nurse_email || "Nurse",
        secondary: "Nurse",
      };
    }
    if (event.actor_id === doc.doctor_id) {
      return {
        primary: doc.doctor_name || doc.doctor_email || "Doctor",
        secondary: "Doctor",
      };
    }
  }
  if (event.actor_email) {
    return {
      primary: event.actor_email,
      secondary: event.actor_role ?? undefined,
    };
  }
  return {
    primary: event.actor_id ? `${event.actor_id.slice(0, 8)}...` : "—",
  };
}
